import createStore from '../../store/index';
import DeliveryOption from '../options/DeliveryOption';
import { PnpMutationsEnum } from '../../store/modules/pnp/mutations';
import PnpItem from '@/models/PnpItem';

class ToolManager {
  private nameAndStyleContainerMap: Map<string, NameAndStyleContainer> = new Map<string, NameAndStyleContainer>();

  constructor() {
    this.populateToolContainerMap();
    this.initializeState();
  }

  toolIsEnabled(identifier): boolean {
    return !createStore.getters.getPnpToolState.get(identifier)!;
  }

  toggleTool(identifier: string): void {
    createStore.commit(PnpMutationsEnum.toggleTool, identifier);
  }

  hideToolsThatShouldNotBeOpen(deliveryOptions: Array<DeliveryOption>) {
    if (!deliveryOptions) {
      return;
    }

    const pnpItemNameArray: string[] = deliveryOptions.map((option) => {
      return option.name;
    });

    Array.from(this.nameAndStyleContainerMap.keys())
      .filter((key) => {
        return !pnpItemNameArray.includes(key);
      })
      .forEach((item) => {
        createStore.commit(PnpMutationsEnum.setHidden, item);
      });
  }

  getPnpArray(): Array<PnpItem> {
    return createStore.getters.getPnpItemArray.filter(
      (item) => this.nameAndStyleContainerMap.get(item.name) !== undefined
    );
  }

  setPnpItemArray(array: Array<DeliveryOption>): void {
    const pnpItemArray: Array<PnpItem> = array.map((deliveryOption) => {
      const pnpItem: PnpItem = new PnpItem(deliveryOption.name);
      deliveryOption.settings.forEach((value: string, key: string) => {
        pnpItem.addAttribute(key, value);
      });
      return pnpItem;
    });

    createStore.commit(PnpMutationsEnum.setPnpItemArray, pnpItemArray);
  }

  private initializeState(): void {
    createStore.commit(
      PnpMutationsEnum.initializeState,
      new Map<string, boolean>(Array.from(this.nameAndStyleContainerMap, ([key]) => key).map((key) => [key, true]))
    );
  }

  getIconClass(identifier: string): string {
    const nameAndStyleContainer = this.nameAndStyleContainerMap.get(identifier);
    if (nameAndStyleContainer) {
      return nameAndStyleContainer.styleClass;
    } else {
      return 'question';
    }
  }

  getTitle(identifier: string, useEnabledTitle = false) {
    const nameAndStyleContainer = this.nameAndStyleContainerMap.get(identifier);
    if (nameAndStyleContainer) {
      return useEnabledTitle ? nameAndStyleContainer.enabledDescription : nameAndStyleContainer.description;
    } else {
      return 'Unknown';
    }
  }

  getKeys() {
    return this.nameAndStyleContainerMap.keys;
  }

  private populateToolContainerMap(): void {
    this.nameAndStyleContainerMap.set('line-reader', new NameAndStyleContainer('Line Reader', 'grip-lines'));
    this.nameAndStyleContainerMap.set(
      'invert-display-polarity',
      new NameAndStyleContainer('Invert Display', 'droplet')
    );
    this.nameAndStyleContainerMap.set('magnification', new NameAndStyleContainer('Magnification', 'magnifying-glass'));
    this.nameAndStyleContainerMap.set('choice_masking', new NameAndStyleContainer('Choice Masking', 'list'));
    this.nameAndStyleContainerMap.set('basic-calculator', new NameAndStyleContainer('Basic Calculator', 'calculator'));
    this.nameAndStyleContainerMap.set(
      'standard-calculator',
      new NameAndStyleContainer('Standard Calculator', 'calculator')
    );
    this.nameAndStyleContainerMap.set(
      'scientific-calculator',
      new NameAndStyleContainer('Scientific Calculator', 'calculator')
    );
    this.nameAndStyleContainerMap.set(
      'graphing-calculator',
      new NameAndStyleContainer('Graphing Calculator', 'calculator')
    );
    this.nameAndStyleContainerMap.set('ruler-on-screen', new NameAndStyleContainer('Ruler', 'ruler'));
    this.nameAndStyleContainerMap.set(
      'protractor-on-screen',
      new NameAndStyleContainer('Protractor', 'ruler-triangle')
    );
    this.nameAndStyleContainerMap.set('dictionary', new NameAndStyleContainer('Dictionary', 'book-open'));
    this.nameAndStyleContainerMap.set('highlighter', new NameAndStyleContainer('Highlighter', 'highlighter'));
    this.nameAndStyleContainerMap.set('highlighter-remove', new NameAndStyleContainer('Remove Highlighter', 'recycle'));
    this.nameAndStyleContainerMap.set('flag_item', new NameAndStyleContainer('Flag item', 'flag', 'Remove flag'));
    this.nameAndStyleContainerMap.set('notepad', new NameAndStyleContainer('Notepad', 'clipboard-list'));
    this.nameAndStyleContainerMap.set(
      'line_reader',
      new NameAndStyleContainer('Line Reader', 'square-poll-horizontal')
    );
    this.nameAndStyleContainerMap.set('color_scheme', new NameAndStyleContainer('Color Scheme', 'palette'));
    this.nameAndStyleContainerMap.set('zoom', new NameAndStyleContainer('Zoom', 'magnifying-glass'));
    this.nameAndStyleContainerMap.set('font_size', new NameAndStyleContainer('Font Size', 'text-height'));
    this.nameAndStyleContainerMap.set('dictionary', new NameAndStyleContainer('Dictionary', 'spell-check'));
  }
}

class NameAndStyleContainer {
  private readonly _description: string;
  private readonly _styleClass: string;
  private readonly _enabledDescription: string;
  constructor(description: string, styleClass: string, enabledDescription: string | undefined = undefined) {
    this._description = description;
    this._styleClass = styleClass;
    this._enabledDescription = enabledDescription ? enabledDescription : description;
  }

  get description(): string {
    return this._description;
  }

  get styleClass(): string {
    return this._styleClass;
  }

  get enabledDescription(): string {
    return this._enabledDescription;
  }
}

export default ToolManager;
